import { useMemo, useState, type ComponentType } from "react";
import { useQuery } from "@tanstack/react-query";

import { formatUnits } from "viem";
import type { EstadoVault } from "../hooks/useVault";
import type { RedKeel } from "../config/networks";
import { sueloTick, tickAPrecio, vistaPrecio } from "../lib/precio";
import { num } from "../lib/formato";
import { Hueco } from "./Modal";
import { PanelComisiones } from "./PanelComisiones";
import { clienteLogs } from "../lib/cliente";
import { EXPLORADOR } from "../lib/enlaces";
import { useEventos } from "../hooks/useEventos";
import { useGas, NOMBRE_MOTIVO } from "../hooks/useGas";
import { IcoDinero, IcoRobot, IcoAjuste, IcoAviso } from "./Iconos";
import { useT } from "../lib/i18n";

/**
 * Historial del vault, en tres zonas que responden las tres preguntas del dueño:
 *   1. GANANCIAS  — cuánto lleva ganado y qué le queda a él (PanelComisiones).
 *   2. GASTOS     — qué costó ganarlo (el gas por operación) y su ganancia REAL (neto).
 *   3. QUÉ PASÓ   — la trayectoria del rango y el timeline de todo lo que ocurrió.
 *
 * Los eventos del REBALANCEADOR se emiten con la dirección del VAULT como origen (lo ejecuta por
 * delegatecall), por eso se filtra por `address: vault`. La lista y la consulta viven en `useEventos`:
 * son los mismos que necesitan el gas y la actividad, y pedirlos tres veces era pedirlos tres veces.
 */
type Clase = "dinero" | "robot" | "ajuste" | "aviso";
type Signo = "pos" | "neg" | "gas" | "";

type Fila = {
  bloque: bigint;
  tx: string;
  indice: number;
  tipo: string;
  clase: Clase;
  nota?: string;
  /** importe a la derecha, con su signo/color */
  val?: { texto: string; signo: Signo };
  /** rango: de → a (chips) */
  flujo?: { de?: string; a: string; etiqueta?: string; extra?: string };
};

const FILTROS: Array<{ k: "todo" | Clase; t: string }> = [
  { k: "todo", t: "Todo" },
  { k: "dinero", t: "Tu dinero" },
  { k: "robot", t: "El robot" },
  { k: "ajuste", t: "Ajustes" },
  { k: "aviso", t: "Avisos" },
];

const GLIFO: Record<Clase, ComponentType<{ size?: number }>> = {
  dinero: IcoDinero, robot: IcoRobot, ajuste: IcoAjuste, aviso: IcoAviso,
};

export function Historial({ v, red }: { v: EstadoVault; red: RedKeel }) {
  const tr = useT();
  const [filtro, setFiltro] = useState<"todo" | Clase>("todo");

  const eventos = useEventos(v.direccion, red);
  const logs = eventos.data;
  const gas = useGas(v.direccion, red);

  const consulta = useQuery({
    queryKey: ["historial-fechas", red.clave, v.direccion, logs?.length ?? 0],
    enabled: !!logs,
    staleTime: 60_000,
    queryFn: async () => {
      const client = clienteLogs(red);
      const DINERO = ["FeesCollected", "PerfFeeTaken", "GasReimbursed", "FeesWithdrawn", "ProfitReinvested"];
      const orden = [...logs!].sort((a, b) => Number(b.blockNumber - a.blockNumber));
      const bloques = [...new Set([
        ...orden.slice(0, 80).map((l) => l.blockNumber),
        ...orden.filter((l) => DINERO.includes(l.eventName as string)).map((l) => l.blockNumber),
      ])].slice(0, 300);
      const fechas = new Map<bigint, number>();
      await Promise.all(
        bloques.map(async (b) => {
          try {
            const bl = await client.getBlock({ blockNumber: b });
            fechas.set(b, Number(bl.timestamp));
          } catch { /* sin fecha: se enseña el bloque */ }
        })
      );
      return { logs: logs!, fechas };
    },
  });

  const filas = useMemo((): Fila[] => {
    if (!consulta.data) return [];
    const { d0, d1, s0, s1 } = {
      d0: v.token0.decimals, d1: v.token1.decimals, s0: v.token0.symbol, s1: v.token1.symbol,
    };
    const vista = vistaPrecio(v.precio, s0, s1);
    const t = (x: bigint | undefined, dec: number) => Number(formatUnits(x ?? 0n, dec));
    const par = (a?: bigint, b?: bigint) => {
      const x = t(a, d0), y = t(b, d1);
      if (x > 0 && y > 0) return `${num(x)} ${s0} + ${num(y)} ${s1}`;
      if (x > 0) return `${num(x)} ${s0}`;
      if (y > 0) return `${num(y)} ${s1}`;
      return tr("nada");
    };
    const ref = (x?: bigint) => `${num(t(x, v.ref.decimals))} ${v.ref.symbol}`;
    const precioDe = (tick: number) => {
      const p = tickAPrecio(tick, d0, d1);
      return num(vista.invertido ? 1 / p : p, 4);
    };
    const rango = (lo: number, hi: number) => {
      const a = precioDe(lo), b = precioDe(hi);
      return vista.invertido ? `${b} – ${a}` : `${a} – ${b}`;
    };
    const rangoDeCentro = (centro: number) =>
      rango(sueloTick(centro - v.halfWidthTicks, v.spacing), sueloTick(centro + v.halfWidthTicks, v.spacing));

    // Pasada cronológica: numera los reajustes (#1 = el primero) y encadena el rango de cada uno con
    // el anterior, para poder pintar «rango viejo → rango nuevo». La lista va del más nuevo al más
    // viejo, así que sin esto no se sabe cuál venía antes.
    const nReajuste = new Map<string, number>();
    const rangoPrevio = new Map<string, string | undefined>();
    let cuenta = 0;
    let ultimoRango: string | undefined;
    for (const l of [...consulta.data.logs].sort(
      (a, b) => Number(a.blockNumber - b.blockNumber) || a.logIndex - b.logIndex
    )) {
      const a = l.args as Record<string, unknown>;
      const key = `${l.transactionHash}-${l.logIndex}`;
      if (l.eventName === "Opened") {
        ultimoRango = rangoDeCentro(Number(a.openTick));
      } else if (l.eventName === "Rebalanced") {
        nReajuste.set(key, ++cuenta);
        rangoPrevio.set(key, ultimoRango);
        ultimoRango = rango(Number(a.tickLower), Number(a.tickUpper));
      } else if (l.eventName === "Recovered") {
        ultimoRango = rango(Number(a.tickLower), Number(a.tickUpper));
      }
    }

    return consulta.data.logs.map((l): Fila => {
      const a = l.args as Record<string, unknown>;
      const base = { bloque: l.blockNumber, tx: l.transactionHash, indice: l.logIndex };
      const key = `${l.transactionHash}-${l.logIndex}`;
      switch (l.eventName) {
        case "Opened":
          return { ...base, clase: "dinero", tipo: tr("Piscina abierta"),
            nota: tr("centrada en {precio} {unidad} · objetivo a recuperar {ref}", { precio: precioDe(Number(a.openTick)), unidad: vista.unidad, ref: ref(a.costBasisRef as bigint) }),
            flujo: { etiqueta: tr("rango inicial"), a: `${rangoDeCentro(Number(a.openTick))} ${vista.unidad}` } };
        case "Rebalanced": {
          const n = nReajuste.get(key);
          return { ...base, clase: "robot", tipo: n ? tr("Reajuste #{n}", { n }) : tr("Reajuste del rango"),
            nota: tr("el precio se había salido; el robot movió el rango"),
            flujo: { de: rangoPrevio.get(key), a: rango(Number(a.tickLower), Number(a.tickUpper)) } };
        }
        case "Recovered":
          return { ...base, clase: "robot", tipo: tr("Recuperación"),
            nota: a.capped
              ? tr("topado por tu límite de estiramiento — no llegó a cubrir todo")
              : tr("en vez de re-centrar y cristalizar la pérdida, se estiró hacia donde el precio tiene que volver"),
            flujo: { etiqueta: tr("rango sesgado"), a: rango(Number(a.tickLower), Number(a.tickUpper)), extra: tr("para devolver {ref}", { ref: ref(a.target as bigint) }) } };
        case "Reinjected":
          return { ...base, clase: "robot", tipo: tr("Reinyección (DCA)"),
            nota: tr("el precio bajó lo bastante para comprar otro tramo · quedan {ref} en reserva", { ref: ref(a.reserveLeft as bigint) }),
            val: { texto: tr("soltó {ref}", { ref: ref(a.amount as bigint) }), signo: "" } };
        case "FeesCollected":
          return { ...base, clase: "dinero", tipo: tr("Comisiones cobradas"),
            nota: a.held ? tr("apartadas para ti · ya sin la comisión de Keellabs") : tr("reinvertidas en la posición"),
            val: { texto: `+${par(a.net0 as bigint, a.net1 as bigint)}`, signo: "pos" } };
        case "PerfFeeTaken":
          return { ...base, clase: "dinero", tipo: tr("Comisión de Keellabs"),
            nota: tr("el 10% de lo que generó la posición, nunca del capital"),
            val: { texto: `−${par(a.fee0 as bigint, a.fee1 as bigint)}`, signo: "neg" } };
        case "Deposited":
          return { ...base, clase: "dinero", tipo: tr("Depósito"),
            nota: tr("entró dinero tuyo al vault"),
            val: { texto: `${num(t(a.amount as bigint, (a.token as string)?.toLowerCase() === v.token0.address.toLowerCase() ? d0 : d1))} ${(a.token as string)?.toLowerCase() === v.token0.address.toLowerCase() ? s0 : s1}`, signo: "pos" } };
        case "Withdrawn":
          return { ...base, clase: "dinero", tipo: tr("Retirada a tu wallet"),
            nota: tr("salió de la posición"),
            val: { texto: par(a.amount0 as bigint, a.amount1 as bigint), signo: "pos" } };
        case "FeesWithdrawn":
          return { ...base, clase: "dinero", tipo: tr("Ganancias retiradas"),
            nota: tr("a tu wallet"),
            val: { texto: par(a.profit0 as bigint, a.profit1 as bigint), signo: "pos" } };
        case "ProfitReinvested":
          return { ...base, clase: "dinero", tipo: tr("Ganancias reinvertidas"),
            nota: tr("volvieron a la posición"),
            val: { texto: par(a.profit0 as bigint, a.profit1 as bigint), signo: "" } };
        case "Swept":
          return { ...base, clase: "robot", tipo: tr("Barrido"),
            nota: tr("capital que estaba parado y volvió a la posición"),
            val: { texto: par(a.amount0 as bigint, a.amount1 as bigint), signo: "" } };
        case "GasReimbursed":
          return { ...base, clase: "robot", tipo: tr("Gas cobrado"),
            nota: tr("el robot adelantó el gas de red y se reembolsó de tu bolsa"),
            val: { texto: `−${par(a.amount0 as bigint, a.amount1 as bigint)}`, signo: "gas" } };
        case "ReserveAdded":
          return { ...base, clase: "dinero", tipo: tr("Reserva recargada"),
            nota: tr("reserva total {ref}", { ref: ref(a.reserve as bigint) }),
            val: { texto: `+${ref(a.amount as bigint)}`, signo: "pos" } };
        case "RebalancerUpgraded":
          return { ...base, clase: "ajuste", tipo: tr("Lógica actualizada"),
            nota: tr("el dueño adoptó una versión nueva del código · {hash}…", { hash: String(a.nuevo).slice(0, 10) }) };
        case "GasBudgetLow":
          return { ...base, clase: "aviso", tipo: tr("Aviso: queda poco gas"),
            nota: tr("con lo que queda no salen ni tres operaciones más"),
            val: { texto: par(a.remaining0 as bigint, a.remaining1 as bigint), signo: "neg" } };
        case "GasShortfall":
          return { ...base, clase: "aviso", tipo: tr("Aviso: la bolsa no cubrió el gas"),
            nota: tr("pidió {want}, pagó {paid} · la diferencia la puso el robot", { want: par(a.want0 as bigint, a.want1 as bigint), paid: par(a.paid0 as bigint, a.paid1 as bigint) }) };
        case "PausedSet":
          return { ...base, clase: "ajuste", tipo: a.paused ? tr("Robot pausado") : tr("Robot reanudado"), nota: tr("lo hizo el dueño") };
        case "ConfigUpdated":
          return { ...base, clase: "ajuste", tipo: tr("Ajustes cambiados"), nota: tr("los cambió el dueño") };
        default:
          return { ...base, clase: "ajuste", tipo: tr("Evento") };
      }
    }).sort((x, y) => Number(y.bloque - x.bloque) || y.indice - x.indice);
  }, [consulta.data, v, tr]);

  // Trayectoria del rango: dónde abrió, cuántas veces se reajustó y en qué rango está ahora.
  const resumen = useMemo(() => {
    if (!consulta.data) return null;
    const d0 = v.token0.decimals, d1 = v.token1.decimals;
    const vista = vistaPrecio(v.precio, v.token0.symbol, v.token1.symbol);
    const precioDe = (tick: number) => {
      const p = tickAPrecio(tick, d0, d1);
      return num(vista.invertido ? 1 / p : p, 4);
    };
    const rango = (lo: number, hi: number) => {
      const a = precioDe(lo), b = precioDe(hi);
      return vista.invertido ? `${b} – ${a}` : `${a} – ${b}`;
    };
    const abierto = consulta.data.logs.find((l) => l.eventName === "Opened");
    const reajustes = consulta.data.logs.filter((l) => l.eventName === "Rebalanced").length;
    const recuperaciones = consulta.data.logs.filter((l) => l.eventName === "Recovered").length;
    const centro = abierto ? Number((abierto.args as Record<string, unknown>).openTick) : 0;
    const apertura = abierto
      ? { rango: rango(sueloTick(centro - v.halfWidthTicks, v.spacing), sueloTick(centro + v.halfWidthTicks, v.spacing)) }
      : null;
    const actual = v.rango ? rango(v.rango.lo, v.rango.hi) : null;
    return { apertura, reajustes, recuperaciones, actual, enRango: v.enRango, unidad: vista.unidad };
  }, [consulta.data, v]);

  // Gastos + neto: lo generado, lo que se llevó Keellabs y lo que costó de gas (todo lifetime, en ref).
  const cuentas = useMemo(() => {
    if (!consulta.data) return null;
    const t = (x: unknown, dec: number) => Number(formatUnits((x as bigint) ?? 0n, dec));
    const enRef = (a0: number, a1: number) =>
      v.refEsToken0 ? a0 + (v.precio > 0 ? a1 / v.precio : 0) : a1 + a0 * v.precio;
    const val = (a: unknown, b: unknown) => enRef(t(a, v.token0.decimals), t(b, v.token1.decimals));
    let bruto = 0, keellabs = 0, gasTotal = 0;
    for (const l of consulta.data.logs) {
      const a = l.args as Record<string, unknown>;
      if (l.eventName === "FeesCollected") bruto += val(a.net0, a.net1);
      else if (l.eventName === "PerfFeeTaken") { const k = val(a.fee0, a.fee1); bruto += k; keellabs += k; }
      else if (l.eventName === "GasReimbursed") gasTotal += val(a.amount0, a.amount1);
    }
    return { bruto, keellabs, gasTotal, neto: Math.max(0, bruto - keellabs - gasTotal) };
  }, [consulta.data, v]);

  const enRefGas = (a0: bigint, a1: bigint) => {
    const x0 = Number(a0) / 10 ** v.token0.decimals;
    const x1 = Number(a1) / 10 ** v.token1.decimals;
    return v.refEsToken0 ? x0 + (v.precio > 0 ? x1 / v.precio : 0) : x1 + x0 * v.precio;
  };

  const visibles = filtro === "todo" ? filas : filas.filter((f) => f.clase === filtro);

  // Agrupadas por transacción: un reajuste cobra comisiones, paga gas y a veces reinyecta, todo en
  // la misma firma. En lista plana parecen cosas sueltas.
  const grupos = useMemo(() => {
    const m = new Map<string, Fila[]>();
    for (const f of visibles) m.set(f.tx, [...(m.get(f.tx) ?? []), f]);
    return [...m.entries()];
  }, [visibles]);

  const fallo = eventos.error ?? consulta.error;
  if (fallo)
    return (
      <div className="aviso error">
        <p>{tr("No pude leer el historial.")}</p>
        <p className="detalle">{fallo.message}</p>
      </div>
    );
  if (eventos.isPending || consulta.isPending) return <Hueco alto={64} n={4} />;
  if (filas.length === 0)
    return <p className="detalle">{tr("Todavía no ha pasado nada en este vault.")}</p>;

  const explorador = EXPLORADOR[red.clave];

  return (
    <div className="historial">
      {/* 1. GANANCIAS */}
      <PanelComisiones v={v} logs={consulta.data!.logs as never} fechas={consulta.data!.fechas} />

      {/* 2. GASTOS */}
      <section className="hist-sec">
        <div className="hist-cab"><span>{tr("Gastos")}</span><span className="ln" /></div>
        {cuentas && (cuentas.bruto > 0 || cuentas.gasTotal > 0) && (
          <div className="neto">
            <div className="eq">
              <b className="tnum">{num(cuentas.bruto)}</b> {tr("cobrado")}
              <span className="op">−</span> <span className="tnum gasc">{num(cuentas.gasTotal)}</span> {tr("gas")}
              <span className="op">−</span> <span className="tnum">{num(cuentas.keellabs)}</span> {tr("comisión")}
              <span className="op">=</span> <span className="res tnum">{num(cuentas.neto)} {v.ref.symbol}</span>
            </div>
            <span className="flecha">{tr("tu ganancia real")}</span>
          </div>
        )}
        <div className="tarjeta gastos">
          <p className="detalle">
            {tr("Cada vez que el robot actúa, adelanta el gas de red y se reembolsa de tu bolsa. Aquí, por qué operación se fue.")}{" "}
            <strong>{tr("La apertura no cuenta")}</strong>: {tr("esa la pagaste tú.")}
          </p>
          {gas.isPending && <Hueco alto={40} n={3} />}
          {gas.isError && <p className="detalle malo">{tr("No pude leer los cobros de gas.")}</p>}
          {gas.data && gas.data.veces === 0 && (
            <p className="detalle">{tr("Todavía no se ha cobrado nada de la bolsa.")}</p>
          )}
          {gas.data && gas.data.veces > 0 && (() => {
            const maxOp = Math.max(...gas.data.filas.map((f) => enRefGas(f.total0, f.total1)), 0) || 1;
            return (
              <table className="gastos-tabla">
                <thead>
                  <tr><th>{tr("Operación")}</th><th className="n">{tr("Veces")}</th><th className="n">{tr("Total")}</th><th className="n">{tr("Media")}</th><th className="barc" /></tr>
                </thead>
                <tbody>
                  {gas.data.filas.map((f) => {
                    const total = enRefGas(f.total0, f.total1);
                    return (
                      <tr key={f.motivo}>
                        <td><span className="op-nom"><span className="dot" />{tr(NOMBRE_MOTIVO[f.motivo])}</span></td>
                        <td className="n tnum">{f.veces}</td>
                        <td className="n tnum">{num(total)} {v.ref.symbol}</td>
                        <td className="n tnum tenue">{num(total / f.veces)}</td>
                        <td className="barc"><div className="gbar" style={{ width: `${Math.max(4, (total / maxOp) * 100)}%` }} /></td>
                      </tr>
                    );
                  })}
                  <tr className="tot">
                    <td>{tr("Total")}</td>
                    <td className="n tnum">{gas.data.veces}</td>
                    <td className="n tnum">{num(enRefGas(gas.data.total0, gas.data.total1))} {v.ref.symbol}</td>
                    <td className="n tnum tenue">{num(enRefGas(gas.data.total0, gas.data.total1) / gas.data.veces)}</td>
                    <td className="barc" />
                  </tr>
                </tbody>
              </table>
            );
          })()}
        </div>
      </section>

      {/* 3. QUÉ HA PASADO */}
      <section className="hist-sec">
        <div className="hist-cab"><span>{tr("Qué ha pasado en la piscina")}</span><span className="ln" /></div>

        {resumen && (resumen.apertura || resumen.actual) && (
          <div className="tray">
            {resumen.apertura && (
              <span className="paso"><b>{tr("Abrió")}</b> {resumen.apertura.rango} {resumen.unidad}</span>
            )}
            <span className="sep">→</span>
            <span className="paso">
              <b>{resumen.reajustes}</b> {resumen.reajustes === 1 ? tr("reajuste") : tr("reajustes")}
              {resumen.recuperaciones > 0 && <> · <b>{resumen.recuperaciones}</b> {resumen.recuperaciones === 1 ? tr("recuperación") : tr("recuperaciones")}</>}
            </span>
            {resumen.actual && (
              <>
                <span className="sep">→</span>
                <span className="paso"><b>{tr("Ahora")}</b> {resumen.actual}{" "}
                  <span className={resumen.enRango ? "dentro" : "fuera"}>{resumen.enRango ? tr("✓ en rango") : tr("⚠ fuera")}</span>
                </span>
              </>
            )}
          </div>
        )}

        <div className="filtros">
          {FILTROS.map((f) => {
            const n = f.k === "todo" ? filas.length : filas.filter((x) => x.clase === f.k).length;
            return (
              <button key={f.k} className={`chip ${filtro === f.k ? "activo" : ""}`}
                onClick={() => setFiltro(f.k)} disabled={n === 0}>
                {tr(f.t)} <b>{n}</b>
              </button>
            );
          })}
        </div>

        <ol className="tl">
          {grupos.map(([tx, fs]) => {
            const claseGrupo = clasePrincipal(fs);
            const Ico = GLIFO[claseGrupo];
            return (
              <li key={tx}>
                <span className={`glifo g-${claseGrupo}`}><Ico /></span>
                <div className="fecha">
                  <span>{cuando(consulta.data!.fechas.get(fs[0]!.bloque), tr)}</span>
                  {fs.length > 1 && <span className="junto">{tr("{n} en 1 tx", { n: fs.length })}</span>}
                  <a href={`${explorador}/tx/${tx}`} target="_blank" rel="noreferrer">{tr("bloque {bloque} ↗", { bloque: String(fs[0]!.bloque) })}</a>
                </div>
                <div className="caja">
                  {fs.map((f) => (
                    <div key={`${f.tx}-${f.indice}`} className="ev">
                      <div className="izq">
                        <span className="tipo">{f.tipo}</span>
                        {f.nota && <div className="nota">{f.nota}</div>}
                        {f.flujo && (
                          <div className="rng-flow">
                            {f.flujo.etiqueta && <span className="et">{f.flujo.etiqueta}</span>}
                            {f.flujo.de && <><span className="chip2">{f.flujo.de}</span> → </>}
                            <span className="chip2 nueva">{f.flujo.a}</span>
                            {f.flujo.extra && <span className="et">{f.flujo.extra}</span>}
                          </div>
                        )}
                      </div>
                      {f.val && <span className={`val ${f.val.signo}`}>{f.val.texto}</span>}
                    </div>
                  ))}
                </div>
              </li>
            );
          })}
        </ol>
      </section>
    </div>
  );
}

// Icono del grupo: manda lo más relevante (un aviso salta sobre un ajuste; el dinero sobre el robot).
function clasePrincipal(fs: Fila[]): Clase {
  const orden: Clase[] = ["aviso", "dinero", "robot", "ajuste"];
  for (const c of orden) if (fs.some((f) => f.clase === c)) return c;
  return "ajuste";
}

function cuando(ts: number | undefined, tr: (es: string, vars?: Record<string, string | number>) => string) {
  if (!ts) return tr("fecha desconocida");
  const d = new Date(ts * 1000);
  const s = Math.floor(Date.now() / 1000) - ts;
  const rel = s < 60 ? tr("hace un momento")
    : s < 3600 ? tr("hace {n} min", { n: Math.floor(s / 60) })
    : s < 86400 ? tr("hace {n} h", { n: Math.floor(s / 3600) })
    : s < 2592000 ? tr("hace {n} días", { n: Math.floor(s / 86400) })
    : d.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" });
  return `${rel} · ${d.toLocaleString(undefined, { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}`;
}
